/**
 * messaging domain (doc 10.3). Function names + paths mirror the messaging
 * route inventory verbatim:
 *   `listChannels()`        → `GET /channels`
 *   `sendMessage(id, body)` → `POST /channels/:id/messages`
 *
 * Types mirror the API's messaging service views (mark for consolidation with
 * the API's exported zod schemas when they are published). Authorization is
 * enforced server-side (`messaging:read` / `messaging:send` / `messaging:manage`
 * plus per-channel membership) — the client never gates.
 */
import { del, get, patch, post, put } from './transport.js';
// --- Channels --------------------------------------------------------------
/** `GET /channels` → `{ channels }` (needs `messaging:read`). */
export async function listChannels(options) {
    const body = await get('/channels', options);
    return body.channels;
}
/** `POST /channels` → `{ channel }` (needs `messaging:manage`). */
export async function createChannel(input, options) {
    const body = await post('/channels', input, options);
    return body.channel;
}
/** `GET /channels/:id` → `{ channel, members }` (needs `messaging:read` + membership). */
export async function getChannel(id, options) {
    return get(`/channels/${id}`, options);
}
/** `PATCH /channels/:id` → `{ channel }` (needs `messaging:manage`). */
export async function updateChannel(id, input, options) {
    const body = await patch(`/channels/${id}`, input, options);
    return body.channel;
}
// --- Members ---------------------------------------------------------------
/** `POST /channels/:id/members` → `{ member }` (needs `messaging:manage`). */
export async function addMember(id, input, options) {
    const body = await post(`/channels/${id}/members`, input, options);
    return body.member;
}
/** `DELETE /channels/:id/members/:userId` → 204 (needs `messaging:manage`). */
export async function removeMember(id, userId, options) {
    await del(`/channels/${id}/members/${userId}`, options);
}
// --- Read state / mute -----------------------------------------------------
/** `PUT /channels/:id/read` → 204 (needs `messaging:send`). */
export async function markRead(id, options) {
    await put(`/channels/${id}/read`, undefined, options);
}
/** `PUT /channels/:id/mute` → 204 (needs `messaging:send`). */
export async function setMuted(id, input, options) {
    await put(`/channels/${id}/mute`, input, options);
}
// --- Messages --------------------------------------------------------------
/** `GET /channels/:id/messages` → `{ messages }` (needs `messaging:read` + membership). */
export async function listMessages(id, query = {}, options) {
    const body = await get(`/channels/${id}/messages`, {
        ...options,
        query: { ...query },
    });
    return body.messages;
}
/**
 * `GET /channels/:id/messages`, keeping the whole page envelope (F193):
 * `messages` plus `nextCursor`/`hasMore`, so a history pager needs neither the
 * full-page heuristic nor a timestamp boundary that can skip a message. The
 * fallbacks make an older server (pre-envelope) read as "no more history"
 * rather than an undefined crash.
 */
export async function listMessagesPage(id, query = {}, options) {
    const body = await get(`/channels/${id}/messages`, {
        ...options,
        query: { ...query },
    });
    return {
        messages: body.messages,
        nextCursor: body.nextCursor ?? null,
        hasMore: body.hasMore ?? false,
    };
}
/**
 * `GET /channels/:id/threads/:rootId` (needs `messaging:read` + membership).
 *
 * The same wall as listing: a channel the caller is not in answers the same 404
 * as one that does not exist. A rootId that is not a root of this channel — a
 * reply's id included — answers "That message is not available."
 */
export async function readThread(channelId, rootId, query = {}, options) {
    return get(`/channels/${channelId}/threads/${rootId}`, {
        ...options,
        query: { ...query },
    });
}
/**
 * `GET /channels/:id/messages/search` (needs `messaging:read` + membership).
 *
 * Channel-scoped only — org-wide search across channels is deliberately not a
 * route; see the server's route header. A channel the caller is not in
 * answers the same 404 as one that does not exist.
 */
export async function searchMessages(id, query, options) {
    const body = await get(`/channels/${id}/messages/search`, { ...options, query: { ...query } });
    return {
        results: body.results,
        nextCursor: body.nextCursor ?? null,
        hasMore: body.hasMore ?? false,
    };
}
/**
 * `GET /messages/mentions` — YOUR unread mentions across your channels
 * (needs `messaging:read`). Own-only: no parameter can widen it. Rows leave
 * the list when you mark the channel read.
 *
 * RETURNS THE PAGE, NOT THE ARRAY. It used to return `body.mentions` and throw
 * `windowExhausted` away at the client boundary, which made the cap invisible
 * to every caller — the classic inert carriage, except the value never even got
 * as far as the screen. Callers that only want the rows read `.mentions`.
 *
 * `windowExhausted ?? false` for an older server that does not send the field:
 * absent is not the same as true, and claiming a floor we were not told about
 * would be its own invention.
 */
export async function listMyMentions(options) {
    const body = await get('/messages/mentions', options);
    return { mentions: body.mentions, windowExhausted: body.windowExhausted ?? false };
}
/** `POST /channels/:id/messages` → `{ message }` (needs `messaging:send` + membership). */
export async function sendMessage(id, input, options) {
    const body = await post(`/channels/${id}/messages`, input, options);
    return body.message;
}
// --- reactions, GIFs and policy --------------------------------------------
/** `POST /messages/:id/reactions` (needs `messaging:send` + channel membership). */
export async function addReaction(messageId, emoji, options) {
    return post(`/messages/${messageId}/reactions`, { emoji }, options);
}
/** `DELETE /messages/:id/reactions` — removes YOUR reaction only. */
export async function removeReaction(messageId, emoji, options) {
    return del(`/messages/${messageId}/reactions`, {
        ...options,
        query: { emoji },
    });
}
/**
 * `GET /messaging/gifs` — search, or trending when `q` is omitted.
 *
 * The query is the ONLY thing that reaches the provider. `available: false` with
 * a reason is a normal answer (no provider configured, provider down, or the
 * organisation switched GIFs off) and never blocks messaging.
 */
export async function searchGifs(q, options) {
    return get('/messaging/gifs', { ...options, query: q ? { q } : {} });
}
/** `GET /messaging/policy` → `{ policy }` (needs `messaging:read`). */
export async function policy(options) {
    const body = await get('/messaging/policy', options);
    return body.policy;
}
/** `PUT /messaging/policy` → `{ policy }` (needs `messaging:manage`). Enforced server-side. */
export async function updatePolicy(input, options) {
    const body = await put('/messaging/policy', input, options);
    return body.policy;
}
/** `PUT /channels/:id/status` — archive or reactivate. Never deletion. */
export async function setChannelStatus(channelId, status, options) {
    return put(`/channels/${channelId}/status`, { status }, options);
}
// --- Automated attribution rendering ----------------------------------------
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * The ONE way an automated message's attribution becomes display text, shared
 * by every reading surface (admin thread, worker thread) so the closed
 * vocabulary renders identically everywhere (AUTOMATION-CAPABILITY-MODEL
 * rule 3):
 *
 *   personal_owner / team_owner → "Automated · set up by {owner}"
 *   organization                → "Automated · organization rule"
 *   assistant                   → "Phibian" (the guard's assistant label)
 *
 * NEVER an identifier: an absent, blank, or uuid-shaped owner name degrades
 * to an honest generic ("a team member") rather than printing an id — even if
 * a server slip ever handed one over. And never free text: the label derives
 * from the structured attribution only, so nothing a sender typed can dress
 * itself as this line.
 */
export function automatedAttributionLabel(automated) {
    if (!automated)
        return 'Automated';
    switch (automated.model) {
        case 'organization':
            return 'Automated · organization rule';
        case 'assistant':
            return 'Phibian';
        case 'personal_owner':
        case 'team_owner': {
            const name = automated.ownerName?.trim() ?? '';
            if (name === '' || UUID_SHAPE.test(name))
                return 'Automated · set up by a team member';
            return `Automated · set up by ${name}`;
        }
        default:
            // A widened/cast model fails closed to the bare marker.
            return 'Automated';
    }
}
//# sourceMappingURL=messaging.js.map